import { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Briefcase, DollarSign, User, Mail, Phone, Lock, ArrowRight, ArrowLeft, Check, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatPhoneNumber, parsePhoneNumber } from '@/lib/formatters';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import formStyles from '@/styles/form.module.css';
import shell from '@/styles/authShell.module.css';
import AuthSplitLayout from '@/components/auth/AuthSplitLayout';
import { SIGNUP_PANEL } from '@/data/authPanels';
import { rememberOauthState } from '@/lib/googleOauthState';
import { roleHome } from '@/lib/roleHome';
import styles from './SignupWizard.module.css';

/**
 * A throwaway password for an account that signs in with Google. It is never
 * shown to anyone and never used; the account simply needs a hash.
 *
 * Not a bare `crypto.randomUUID()`: that global is missing outside a secure
 * context and in jsdom, where it throws a ReferenceError rather than returning
 * undefined — which surfaced as "Signup failed. Please try again." and lost the
 * signup entirely. Degrade instead of throwing.
 */
const generatedPassword = () => {
  const webCrypto = typeof window !== 'undefined' ? window.crypto : undefined;
  if (typeof webCrypto?.randomUUID === 'function') return webCrypto.randomUUID();
  if (typeof webCrypto?.getRandomValues === 'function') {
    return Array.from(webCrypto.getRandomValues(new Uint8Array(24)),
      (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `g-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { n: 1, label: 'Choose Role' },
  { n: 2, label: 'Create Account' },
  { n: 3, label: 'Confirm' },
];

const SignupWizard = () => {
  usePageMeta(PAGE_META.signup);

  const navigate = useNavigate();
  const location = useLocation();
  const prefilledData = location.state || {};

  // Detect referral flow (from ReferralClaim navigation or sessionStorage persistence)
  const referralToken = (typeof window !== 'undefined') ? sessionStorage.getItem('referral_token') : null;
  const referralPrefill = (() => {
    try { return JSON.parse(sessionStorage.getItem('referral_prefill') || '{}'); }
    catch { return {}; }
  })();
  const fromReferral = !!referralToken;

  // The role is chosen at step 1, before the trip out to Google, so it has to
  // survive the redirect. handleGoogleSignup has always written it to
  // localStorage but nothing ever read it back, and GoogleCallback does not
  // pass a role either — so a Google arrival filled in the whole wizard, hit
  // "Complete Signup", failed the `!selectedRole` guard and was thrown back to
  // step 1 with no account created and nothing to show for it. Scoped to the
  // Google return so an abandoned attempt cannot preselect a role for some
  // later, unrelated signup.
  const roleBeforeRedirect = (() => {
    if (!prefilledData.fromGoogle) return '';
    try { return localStorage.getItem('signup_role') || ''; } catch { return ''; }
  })();
  // When coming from referral, lock role to client
  const preselectedRole = fromReferral ? 'client' : (prefilledData.role || roleBeforeRedirect);
  const referralFullName = [referralPrefill.firstName, referralPrefill.lastName].filter(Boolean).join(' ').trim();
  // Skip to step 2 only when the role is actually known. Arriving from the
  // login page's Google button nothing ever chose one, and jumping ahead only
  // defers the failure to the last button.
  //
  // A Google client skips step 2 outright: it asks for a name and email Google
  // already gave us, a phone that is optional on this path, and a password they
  // will never use because they sign in with Google. Funders and lawyers still
  // go through it — that is where company name and firm are collected.
  const [step, setStep] = useState(() => {
    if (!preselectedRole) return 1;
    if (prefilledData.fromGoogle && preselectedRole === 'client') return 3;
    return 2;
  });
  const [selectedRole, setSelectedRole] = useState(preselectedRole);
  const [formData, setFormData] = useState({
    name: prefilledData.name || referralFullName || '',
    email: prefilledData.email || referralPrefill.email || '',
    phone: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    firm_id: '',
    firm_name: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const termsRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [firmSearchResults, setFirmSearchResults] = useState([]);
  const [firmSearchQuery, setFirmSearchQuery] = useState('');
  const [showFirmDropdown, setShowFirmDropdown] = useState(false);
  const googleEmail = prefilledData.email;
  // Whether this account originated at Google, read from the router state
  // GoogleCallback navigates with. There was once an `authMethod` state used
  // for this, but the wizard rewrote it mid-flow, so it disagreed with itself
  // depending on which step the visitor came through. Router state does not
  // change under us. Phone is optional on this path.
  const fromGoogleAuth = !!prefilledData.fromGoogle && !!googleEmail;

  const roles = [
    { id: 'client', title: 'Client / Plaintiff', icon: User, desc: 'Apply for pre-settlement funding' },
    { id: 'lawyer', title: 'Attorney', icon: Briefcase, desc: 'Verify cases and manage client applications' },
    { id: 'funder', title: 'Funder', icon: DollarSign, desc: 'Browse and fund pre-settlement cases' },
  ];

  const handleRoleSelect = (roleId) => {
    setSelectedRole(roleId);
    setTimeout(() => setStep(2), 200);
  };

  const handleGoogleSignup = async () => {
    if (!selectedRole) { toast.error('Please select a role first'); return; }
    localStorage.setItem('signup_role', selectedRole);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + '/auth/google/callback';
    try {
      const response = await fetch(`${API}/auth/google/login?redirect_uri=${encodeURIComponent(redirectUri)}`);
      const data = await response.json();
      if (data.auth_url) {
        // Hold the CSRF state so GoogleCallback can check what Google returns.
        rememberOauthState(data.state);
        window.location.href = data.auth_url;
      } else {
        toast.error('Failed to initiate Google signup');
      }
    } catch (error) {
      toast.error('Failed to connect to Google');
    }
  };

  const handleEmailSignup = async () => {
    if (!formData.name || !formData.email || !formData.password) { toast.error('Please fill in all fields'); return; }
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (formData.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (selectedRole === 'funder' && !formData.company_name.trim()) { toast.error('Company name is required for funders'); return; }
    setStep(3);
  };

  const searchFirms = async (query) => {
    setFirmSearchQuery(query);
    if (query.length < 2) { setFirmSearchResults([]); setShowFirmDropdown(false); return; }
    try {
      const response = await fetch(`${API}/law-firms/search?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        setFirmSearchResults(data.firms || []);
        setShowFirmDropdown(true);
      }
    } catch (error) { console.error('Firm search failed:', error); }
  };

  const selectFirm = (firm) => {
    setFormData(prev => ({ ...prev, firm_id: firm.firm_id, firm_name: firm.name, company_name: firm.name }));
    setFirmSearchQuery(firm.name);
    setShowFirmDropdown(false);
  };

  const clearFirm = () => {
    setFormData(prev => ({ ...prev, firm_id: '', firm_name: '', company_name: '' }));
    setFirmSearchQuery('');
  };

  const handleCompleteSignup = async () => {
    if (!termsAccepted) {
      // Previously the button was simply disabled, so this branch was
      // unreachable and nothing told the user why they were stuck. Mark the
      // control, move focus to it, and let the toast explain.
      setTermsError(true);
      termsRef.current?.focus();
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    if (!selectedRole) { toast.error('Please select a role first'); setStep(1); return; }
    setLoading(true);
    try {
      // One submit path. There used to be a second, guarded by
      // `authMethod === 'google'`, and it caused two bugs: while the only
      // route to step 3 set authMethod to 'email' it was unreachable, so the
      // id_token attached there never shipped; once a Google client skipped
      // step 2 it woke up and bypassed the auto-login below. Branching on
      // fromGoogleAuth — router state that nothing rewrites — is the whole
      // difference, so there is no second path to fall out of step with.
      //
      // A Google client never sees the password field, so mint one. They sign
      // in with Google and never use it; the account still needs a hash.
      const password = formData.password || (fromGoogleAuth ? generatedPassword() : '');
      const signupData = { email: formData.email, password, name: formData.name, role: selectedRole, terms_agreed: true, company_name: formData.company_name || null, firm_id: formData.firm_id || null, referral_token: referralToken || null, phone: formData.phone.trim() || null, google_id_token: fromGoogleAuth ? (prefilledData.idToken || null) : null };
      if (!signupData.email || !signupData.password || !signupData.name || !signupData.role) { toast.error('Please fill in all required fields'); setLoading(false); return; }
      // Required unless the account came from Google, whose visitors may never
      // have been shown the phone field at all.
      if (!fromGoogleAuth && !signupData.phone) { toast.error('Please enter a phone number'); setLoading(false); return; }
      const response = await fetch(`${API}/auth/signup`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(signupData) });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        // The account exists now, so the role carried across the Google
        // redirect has done its job. Cleared here rather than in each branch
        // below so no exit path can leave it behind.
        try { localStorage.removeItem('signup_role'); } catch { /* ignore */ }
        // Referral signup path: backend has auto-verified the email and returned an access_token
        if (data.access_token && data.referral_linked_application_id) {
          localStorage.setItem('access_token', data.access_token);
          // Snapshot referral context for the welcome toast on the dashboard
          let refPrefill = {};
          try { refPrefill = JSON.parse(sessionStorage.getItem('referral_prefill') || '{}'); } catch { /* ignore */ }
          sessionStorage.setItem('referral_welcome', JSON.stringify({
            applicationId: data.referral_linked_application_id,
            attorneyName: refPrefill.lawyerName || '',
            lawFirmName: refPrefill.lawFirmName || '',
            firstName: refPrefill.firstName || (data.user?.name || '').split(' ')[0] || ''
          }));
          sessionStorage.removeItem('referral_token');
          sessionStorage.removeItem('referral_prefill');
          navigate('/client');
          return;
        }
        // Funder or Lawyer signup → both require staff verification before login
        if (selectedRole === 'funder' || selectedRole === 'lawyer') {
          sessionStorage.setItem('pending_approval_signup', selectedRole);
          navigate('/login', { state: { pendingApprovalRole: selectedRole } });
          return;
        }
        // Google client: the backend verified the address and issued a
        // session, so go straight to the dashboard rather than asking them to
        // sign in again with the account they just proved they own.
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
          localStorage.removeItem('signup_role');
          toast.success('Account created!');
          // Routed from the account the backend actually made, not hardcoded.
          // Only clients reach here today — funders and lawyers are held for
          // approval above, and the backend issues a session for clients
          // only — but nothing in this line should depend on that holding.
          navigate(roleHome(data.user?.role, '/client'), { replace: true, state: { user: data.user } });
          return;
        }
        toast.success('Account created! Please check your email to verify your account.', { duration: 6000 });
        navigate('/login', fromReferral ? { state: { fromReferral: true } } : undefined);
      } else {
        const error = await response.json();
        if (error.detail?.includes('already exists')) toast.error('An account with this email already exists.');
        else if (Array.isArray(error.detail)) toast.error(`Missing or invalid fields: ${error.detail.map(e => e.loc?.[1] || e.msg).join(', ')}`);
        else toast.error(error.detail || 'Signup failed.');
      }
    } catch { toast.error('Signup failed. Please try again.'); }
    finally { setLoading(false); }
  };


  const back = (to) => (
    <button type="button" className={styles.back} onClick={() => setStep(to)}>
      <ArrowLeft size={15} aria-hidden="true" /> Back
    </button>
  );

  return (
    <AuthSplitLayout
      testId="signup-page"
      {...SIGNUP_PANEL}
      wide
      topRight={
        <>
          <span>Already have an account?</span>
          <button
            type="button"
            className={`${shared.btn} ${shared.btnQuiet}`}
            onClick={() => navigate('/login')}
            data-testid="signin-link"
          >
            Sign in
          </button>
        </>
      }
    >
      <ol className={styles.progress} data-testid="signup-progress">
        {STEPS.map(({ n, label }) => (
          <li
            key={n}
            className={`${styles.step} ${step >= n ? styles.stepReached : ''}`}
            aria-current={step === n ? 'step' : undefined}
            data-testid={`signup-step-${n}`}
          >
            <span className={`${styles.dot} ${step > n ? styles.dotDone : step === n ? styles.dotActive : ''}`}>
              {step > n ? <Check size={16} aria-hidden="true" /> : n}
            </span>
            <span className={`${styles.stepLabel} ${step >= n ? styles.stepLabelActive : ''}`}>{label}</span>
          </li>
        ))}
      </ol>

      {/* Step 1: Role */}
      {step === 1 && (
        <div data-testid="step-1-role">
          <div className={shell.headStart}>
            <h1 className={shell.title}>Choose Your Role</h1>
            <p className={shell.subtitle}>Select how you&apos;ll use MyCaseLoan</p>
          </div>
          <div className={styles.roles}>
            {roles.map((r) => (
              <button
                key={r.id}
                type="button"
                className={styles.role}
                onClick={() => handleRoleSelect(r.id)}
                data-testid={`role-${r.id}`}
              >
                <span className={styles.roleIcon}><r.icon size={22} aria-hidden="true" /></span>
                <span className={styles.roleBody}>
                  <span className={styles.roleTitle}>{r.title}</span>
                  <span className={styles.roleDesc}>{r.desc}</span>
                </span>
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Account */}
      {step === 2 && (
        <div data-testid="step-2-auth">
          {fromReferral && (
            <div className={shell.banner} data-testid="referral-banner">
              <strong>Welcome!</strong> {referralPrefill.lawyerName ? `${referralPrefill.lawyerName} ` : 'Your attorney '}
              {referralPrefill.lawFirmName ? `at ${referralPrefill.lawFirmName} ` : ''}
              referred you. We&apos;ll link your application automatically once you sign in.
            </div>
          )}
          {!fromReferral && back(1)}

          <div className={shell.headStart}>
            <h1 className={shell.title}>Create Your Account</h1>
            <p className={shell.subtitle}>
              Signing up as <strong>{selectedRole === 'lawyer' ? 'Attorney' : selectedRole}</strong>
            </p>
          </div>

          <div className={formStyles.field}>
            <label htmlFor="name" className={formStyles.label}>Full Name</label>
            <div className={formStyles.adorned}>
              <User size={17} className={formStyles.adornedIcon} aria-hidden="true" />
              <input id="name" type="text" className={`${formStyles.control} ${formStyles.withIcon}`}
                value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Jane Smith" data-testid="input-name" />
            </div>
          </div>

          <div className={formStyles.field}>
            <label htmlFor="email" className={formStyles.label}>Email Address</label>
            <div className={formStyles.adorned}>
              <Mail size={17} className={formStyles.adornedIcon} aria-hidden="true" />
              <input id="email" type="email" className={`${formStyles.control} ${formStyles.withIcon}`}
                value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com" data-testid="input-email" />
            </div>
          </div>

          <div className={formStyles.field}>
            <label htmlFor="phone" className={formStyles.label}>
              Phone Number{fromGoogleAuth && <span className={formStyles.optional}> (optional)</span>}
            </label>
            <div className={formStyles.adorned}>
              <Phone size={17} className={formStyles.adornedIcon} aria-hidden="true" />
              {/* Digits in state, formatted for display — the same split the
                  dashboards use, so a number entered here and one edited later
                  are stored identically. */}
              <input id="phone" type="tel" inputMode="tel" autoComplete="tel"
                className={`${formStyles.control} ${formStyles.withIcon}`}
                value={formatPhoneNumber(formData.phone)}
                onChange={(e) => setFormData({ ...formData, phone: parsePhoneNumber(e.target.value) })}
                placeholder="(555) 123-4567" data-testid="input-phone" />
            </div>
          </div>

          <div className={formStyles.field}>
            <label htmlFor="password" className={formStyles.label}>Password</label>
            <div className={formStyles.adorned}>
              <Lock size={17} className={formStyles.adornedIcon} aria-hidden="true" />
              <input id="password" type={showPassword ? 'text' : 'password'}
                className={`${formStyles.control} ${formStyles.withIcon}`}
                value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Min 8 chars, 1 uppercase, 1 number" data-testid="input-password" />
              <button type="button" className={formStyles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>

          <div className={formStyles.field}>
            <label htmlFor="confirmPassword" className={formStyles.label}>Confirm Password</label>
            <div className={formStyles.adorned}>
              <Lock size={17} className={formStyles.adornedIcon} aria-hidden="true" />
              <input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'}
                className={`${formStyles.control} ${formStyles.withIcon}`}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Re-enter password" data-testid="input-confirm-password" />
              <button type="button" className={formStyles.passwordToggle}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>

          {selectedRole === 'lawyer' && (
            <div className={`${formStyles.field} ${styles.firmWrap}`}>
              <label htmlFor="firm-search" className={formStyles.label}>Law Firm</label>
              <input id="firm-search" type="text" className={formStyles.control} value={firmSearchQuery}
                onChange={(e) => { searchFirms(e.target.value); setFormData({ ...formData, firm_id: '', firm_name: '' }); }}
                placeholder="Search for your law firm..." data-testid="input-firm-search" autoComplete="off" />
              {showFirmDropdown && (
                <div className={styles.firmDropdown} data-testid="firm-dropdown">
                  {firmSearchResults.map((firm) => (
                    <button key={firm.firm_id} type="button" className={styles.firmOption} onClick={() => selectFirm(firm)}>
                      {firm.name}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`${styles.firmOption} ${styles.firmOptionOther}`}
                    onClick={() => { setShowFirmDropdown(false); setFormData({ ...formData, firm_id: 'other', firm_name: firmSearchQuery, company_name: firmSearchQuery }); }}
                  >
                    Not listed — use &ldquo;{firmSearchQuery}&rdquo;
                  </button>
                </div>
              )}
              {formData.firm_id && formData.firm_id !== 'other' && (
                <div className={styles.firmChosen}>
                  <Check size={14} aria-hidden="true" /> {formData.firm_name}
                  <button type="button" className={styles.firmClear} onClick={clearFirm}>(change)</button>
                </div>
              )}
              {formData.firm_id === 'other' && (
                <div className={formStyles.field}>
                  <input type="text" className={formStyles.control} value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Enter your firm name" data-testid="input-firm-other" />
                </div>
              )}
            </div>
          )}

          {selectedRole === 'funder' && (
            <div className={formStyles.field}>
              <label htmlFor="company-name" className={formStyles.label}>Company Name *</label>
              <input id="company-name" type="text" className={formStyles.control} value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Your funding company name" data-testid="input-company-name" />
            </div>
          )}

          <button type="button" className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
            onClick={handleEmailSignup} data-testid="continue-email-btn">
            Continue
          </button>

          <div className={shell.divider}>or</div>

          <button type="button" className={`${shared.btn} ${shared.btnQuiet} ${shell.fullBtn}`}
            onClick={handleGoogleSignup} data-testid="continue-google-btn">
            <svg className={shell.googleIcon} viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && (
        <div data-testid="step-3-confirm">
          {back(2)}

          <div className={shell.headStart}>
            <h1 className={shell.title}>Almost There!</h1>
            <p className={shell.subtitle}>Review and accept our terms to complete signup</p>
          </div>

          <div className={styles.summary}>
            <div className={styles.summaryTitle}>Account Summary</div>
            {Object.entries({
              // Capitalised here rather than by text-transform on the row: that
              // rule also title-cased the email, and would mangle a name like
              // "McDonald" too.
              Role: selectedRole === 'lawyer' ? 'Attorney'
                : selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1),
              Name: formData.name,
              // Addresses are case-insensitive and the backend lower-cases on
              // save, so showing it any other way misrepresents the account
              // that is about to be created.
              Email: formData.email.trim().toLowerCase(),
              ...(formData.phone ? { Phone: formatPhoneNumber(formData.phone) } : {}),
              ...(formData.company_name ? { Company: formData.company_name } : {}),
            }).map(([k, v]) => (
              <div key={k} className={styles.summaryRow}>
                <span className={styles.summaryKey}>{k}</span>
                <span className={styles.summaryValue}>{v}</span>
              </div>
            ))}
          </div>

          <label className={styles.terms}>
            <input
              ref={termsRef}
              type="checkbox"
              checked={termsAccepted}
              className={styles.srOnly}
              aria-invalid={termsError}
              aria-describedby={termsError ? 'terms-error' : undefined}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                if (e.target.checked) setTermsError(false);
              }}
              data-testid="terms-checkbox"
            />
            <span
              className={`${styles.termsBox} ${termsAccepted ? styles.termsBoxChecked : ''} ${termsError ? styles.termsBoxError : ''}`}
            >
              {termsAccepted && <Check size={13} strokeWidth={3} aria-hidden="true" />}
            </span>
            {/* New tab, not a navigation. The wizard holds its state in memory,
                so leaving this page to read the terms would discard the role,
                name, email and phone the user has just entered. stopPropagation
                stays because these sit inside the checkbox's own <label>. */}
            <span className={styles.termsText}>
              I agree to the{' '}
              <a
                href="/terms"
                className={shell.link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Terms of Service (opens in a new tab)"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a
                href="/privacy"
                className={shell.link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Privacy Policy (opens in a new tab)"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </a>
            </span>
          </label>

          {termsError && (
            <p className={styles.termsErrorText} id="terms-error" role="alert" data-testid="terms-error">
              <AlertCircle size={14} aria-hidden="true" />
              You need to accept the Terms of Service and Privacy Policy to continue.
            </p>
          )}

          <button type="button" className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
            onClick={handleCompleteSignup} disabled={loading} data-testid="complete-signup-btn">
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </div>
      )}

      <p className={shell.footNote}>
        &copy; {new Date().getFullYear()} MyCaseLoan. All rights reserved.
      </p>
    </AuthSplitLayout>
  );
};

export default SignupWizard;
